import { useContext, useEffect, useState, useRef, useMemo } from 'react';

import { RedoOutlined, DoubleRightOutlined, RightOutlined } from '@ant-design/icons';
import { decryptSensitiveData } from '@lcsc-design/utils/crypto';
import { BasicRound, MultiAccurate } from '@lcsc-design/utils/decimal';
import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import indexBuriedPoint, { indexBuriedPointActivityTabsMap } from '@lcsc-ui/utils/_indexBuriedPoint';
import { env } from '@lcsc-ui/utils/baseUrl';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import { isNotEmpty } from '@lcsc-ui/utils/common';
import Domains from '@lcsc-ui/utils/domains';
import { unitConversion } from '@lcsc-ui/utils/helper';
import sendAxios from '@lcsc-ui/utils/request';
import Utils from '@lcsc-ui/utils/Utils';
import { Button, Table, message } from 'antd';
import dayjs from 'dayjs';
import { cloneDeep } from 'lodash-es';
import Image from 'next/image';
import { styled } from 'styled-components';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import API from '@/asyncApi/home';
import FlashButton from '@/components/FlashButton';
import QuickFindMaterialComponent from '@/features/homeComponents/components/QuickFindMaterialComponent';
import GoBuyDialog from '@/features/homeComponents/goBuyDialog';
import BuyGroupProductDialog from '@/features/homeComponents/LtBuyDialog';

import 'swiper/css';
import 'swiper/css/navigation';

const activityTypeSpmMap = {
  stockpile: 'stk',
  push_material: 'lt',
  hot: 'hot',
  discount: 'zk',
  clearance: 'qc',
  rebate: 'fl',
  mro_hot: 'mro',
  great_voyage: 'gv',
};

const SkeletonItem = styled.li`
  position: relative;
  width: 100%;
  height: 29px;
  background: #f0f0f0;
  border-radius: 4px;
  margin-bottom: 16px;
  overflow: hidden;
  &::after {
    position: absolute;
    inset: 0 -150%;
    background: linear-gradient(
      90deg,
      rgba(190, 190, 190, 0.2) 25%,
      rgba(129, 129, 129, 0.24) 37%,
      rgba(190, 190, 190, 0.2) 63%
    );
    animation: skeleton-item-loading 1.4s ease infinite;
    content: '';
  }
  @keyframes skeleton-item-loading {
    0% {
      transform: translate(-37.5%);
    }

    to {
      transform: translate(37.5%);
    }
  }
`;

let prevList = [];
let mroBkPrevList = [];

const Empty = ({ emptyTips }) => (
  <div className="w-full mt-[180px] mx-auto text-center">
    <div className="w-[200px] mx-auto">
      <Image
        className="ml-[60px]"
        width={112}
        height={84}
        src="https://static.szlcsc.com/ecp/assets/newWeb/item/null.svg"
        alt="暂无内容图标"
        preview={false}
      />
      {emptyTips && <p className="text-[#ABB4BE] text-[14px] ml-[10px] mt-[16px]">{emptyTips}</p>}
    </div>
  </div>
);

const Loading = ({ isLg }) => {
  return (
    <>
      <div className="flex justify-between">
        {new Array(isLg ? 3 : 2).fill(1).map((item, k) => {
          return (
            <ul key={k} className={`${isLg ? 'w-[32%]' : 'w-[49%]'}`}>
              {new Array(11).fill(1).map((item, key) => (
                <SkeletonItem key={key} />
              ))}
            </ul>
          );
        })}
      </div>
    </>
  );
};

const TableDataComponent = ({ activityData, tableType, isLg, handleClickGoBuy, spmType }) => {
  const { resourceData } = useContext(LayoutContext);
  const { activityType } = activityData;
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const [pageInfo, setPageInfo] = useState({ currentPage: 1 });
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const typeMap = {
    lt: {
      api: '/activity/lt?activityUrl={activityCode}',
      reg: /\/lt\d+\.html/,
      columns: [
        {
          title: '品牌',
          width: isLg ? 150 : 190,
          dataIndex: 'lightBrandName',
          key: 'lightBrandName',
          ellipsis: true,
          render: (text, productItem, idx) => {
            return (
              <a
                href={`${resourceData.projectConfigVO.lcscListUrl}/brand/${productItem.productVO.productGradePlateId}.html`}
                target="_blank"
                title={productItem.productVO.productGradePlateName}
                data-spm={'pp' + (idx + 1)}
              >
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  {productItem.productVO.productGradePlateName}
                </span>
              </a>
            );
          },
        },
        {
          title: '型号',
          width: isLg ? 150 : 190,
          dataIndex: 'productModel',
          key: 'productModel',
          ellipsis: true,
          render: (text, productItem, idx) => {
            return (
              <a
                href={`${activityData?.activityLink}?firstCode=${productItem?.productVO?.productCode}`}
                target="_blank"
                title={productItem.productVO.productModel}
                data-spm={'xh' + (idx + 1)}
              >
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  {productItem.productVO.productModel}
                </span>
              </a>
            );
          },
        },
        {
          title: '立推库存',
          width: isLg ? 150 : 190,
          dataIndex: 'groupActivityProductVO.surplusRecommendNumber',
          key: 'groupActivityProductVO.surplusRecommendNumber',
          ellipsis: true,
          render: (text, productItem) => {
            return (
              <div className="flex items-center justify-between leading-[24px]">
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  {unitConversion(productItem.groupActivityProductVO?.surplusRecommendNumber)}
                </span>
                {hoveredRowKey === productItem.productVO.productId && (
                  <button
                    className="size-[24px] bg-[url(https://static.szlcsc.com/ecp/assets/newWeb/www/home/cart-icon-1.svg)] bg-[length:24px_24px] rounded-[50%] shadow-[0_3px_6px_0px_rgba(0,0,0,0.12),0_1px_2px_-2px_rgba(0,0,0,0.16)]"
                    onClick={() => handleClickGoBuy(productItem)}
                  ></button>
                )}
              </div>
            );
          },
        },
      ],
    },
    bk: {
      api: '/hot/list',
      reg: null,
      columns: [
        {
          title: '品牌',
          dataIndex: 'brandName',
          key: 'brandName',
          width: isLg ? 150 : 190,
          ellipsis: true,
          render: (text, productItem, idx) => {
            return (
              <a
                href={`${resourceData.projectConfigVO.lcscListUrl}/brand/${productItem.productVO.productGradePlateId}.html`}
                target="_blank"
                title={productItem.productVO.productGradePlateName}
                data-spm={'pp' + (idx + 1)}
              >
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  {productItem.productVO.productGradePlateName}
                </span>
              </a>
            );
          },
        },
        {
          title: '型号',
          dataIndex: 'productModel',
          key: 'productModel',
          width: isLg ? 150 : 190,
          ellipsis: true,
          render: (text, productItem, idx) => {
            return (
              <a
                href={`${resourceData.projectConfigVO.lcscListUrl}/hot.html?firstCode=${productItem?.productVO?.productCode}`}
                target="_blank"
                title={productItem.productVO.productModel}
                data-spm={'xh' + (idx + 1)}
              >
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  {productItem.productVO.productModel}
                </span>
              </a>
            );
          },
        },
        {
          title: '价格',
          dataIndex: 'price',
          key: 'price',
          width: isLg ? 150 : 190,
          ellipsis: true,
          render: (text, productItem) => {
            const originalPrice = productItem.productVO.productPriceList.length
              ? productItem.productVO.productPriceList[productItem.productVO.productPriceList.length - 1].thePrice
              : 0;
            let lowPrice = originalPrice || 0;
            if (
              !Utils.isEmpty(productItem.productVO.productPriceDiscountList) &&
              Utils.isEmpty(productItem.productVO.discountBuyLimitProduct) &&
              !productItem.isProductRebate
            ) {
              lowPrice = Math.min(...productItem.productVO.productPriceDiscountList[0].priceList.map((t) => t.price));
            }
            return (
              <div className="flex items-center justify-between leading-[24px] relative">
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  <span>￥{lowPrice}</span>
                  {lowPrice !== originalPrice && (
                    <span className="ml-[16px] text-[12px] line-through text-[#BFBFBF]">￥{originalPrice}</span>
                  )}
                </span>
                {hoveredRowKey === productItem.productVO.productId && (
                  <button
                    className="absolute right-0 top-0 size-[24px] bg-[url(https://static.szlcsc.com/ecp/assets/newWeb/www/home/cart-icon-1.svg)] bg-[length:24px_24px] rounded-[50%] shadow-[0_3px_6px_0px_rgba(0,0,0,0.12),0_1px_2px_-2px_rgba(0,0,0,0.16)]"
                    onClick={() => handleClickGoBuy(productItem, activityType)}
                  ></button>
                )}
              </div>
            );
          },
        },
      ],
    },
    qc: {
      api: '/zk{activityCode}/page',
      reg: /\/zk\d+\.html/,
      columns: [
        {
          title: '品牌',
          dataIndex: 'brandName',
          key: 'brandName',
          width: isLg ? 150 : 190,
          ellipsis: true,
          render: (text, productItem, idx) => {
            return (
              <a
                href={`${resourceData.projectConfigVO.lcscListUrl}/brand/${productItem.productVO.productGradePlateId}.html`}
                target="_blank"
                title={productItem.lightBrandName}
                data-spm={'pp' + (idx + 1)}
              >
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  {productItem.lightBrandName ||
                    productItem.hltProductGradePlateName ||
                    productItem.productGradePlateNameWithoutHighlight}
                </span>
              </a>
            );
          },
        },
        {
          title: '型号',
          dataIndex: 'productModel',
          key: 'productModel',
          width: isLg ? 150 : 190,
          ellipsis: true,
          render: (text, productItem, idx) => {
            return (
              <a
                href={`${activityData?.activityLink}?firstCode=${productItem?.productVO?.productCode}`}
                target="_blank"
                title={productItem.lightProductModel}
                data-spm={'xh' + (idx + 1)}
              >
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  {productItem.lightProductModel || productItem.productModel}
                </span>
              </a>
            );
          },
        },
        {
          //  discount\clearance\rebate
          title: () => (
            <span>
              <span>{['discount', 'clearance'].includes(activityType) ? '折扣价' : '价格'}</span>
              {activityType !== 'discount' && (
                <span className="inline-block text-center leading-[20px] rounded-[4px] w-[60px] h-[22px] border border-solid border-[#FFCCC7] ml-[4px] text-[12px] text-[#FF4D4F]">
                  {activityType === 'rebate' ? '返采购晶' : ''}
                </span>
              )}
            </span>
          ),
          dataIndex: 'price',
          key: 'price',
          width: isLg ? 150 : 190,
          ellipsis: true,
          render: (text, productItem) => {
            // 原价,只有类型为【折扣、清仓】,且有折扣才显示
            const originalPrice =
              ['clearance', 'discount'].includes(activityType) &&
              !Utils.isEmpty(productItem?.priceDiscount?.priceList || []) &&
              productItem.productVO.productPriceList[productItem.productVO.productPriceList.length - 1]?.thePrice;

            // 折扣价,如果没有折扣价,该位置就显示原价
            let discountPrice =
              productItem.productVO.productPriceList[productItem.productVO.productPriceList.length - 1]?.thePrice;
            if (!Utils.isEmpty(productItem?.priceDiscount?.priceList || [])) {
              discountPrice = Math.min(...productItem.priceDiscount.priceList.map((t) => t.price));
            }

            return (
              <div className="flex items-center justify-between leading-[24px] relative">
                <span className={`${hoveredRowKey === productItem.productVO.productId ? 'text-lcBlue' : 'text-black'}`}>
                  <span>
                    {activityType === 'rebate' ? (
                      <span className="inline-block min-w-[78px]">
                        ￥
                        {
                          productItem.productVO.productPriceList[productItem.productVO.productPriceList.length - 1]
                            ?.thePrice
                        }
                      </span>
                    ) : (
                      <>
                        ￥{discountPrice}
                        {originalPrice && originalPrice !== discountPrice && (
                          <span className="ml-[16px] text-[12px] line-through text-[#BFBFBF]">￥{originalPrice}</span>
                        )}
                      </>
                    )}
                    {activityType === 'rebate' && productItem.rebate !== '0' && !productItem.isGuidePrice && (
                      <span className="text-[12px] text-[#FF4D4F]">返{productItem.rebate}%</span>
                    )}
                  </span>
                </span>
                {hoveredRowKey === productItem.productVO.productId && (
                  <button
                    className="absolute right-0 top-0 size-[24px] bg-[url(https://static.szlcsc.com/ecp/assets/newWeb/www/home/cart-icon-1.svg)] bg-[length:24px_24px] rounded-[50%] shadow-[0_3px_6px_0px_rgba(0,0,0,0.12),0_1px_2px_-2px_rgba(0,0,0,0.16)]"
                    onClick={() => handleClickGoBuy(productItem, activityType)}
                  ></button>
                )}
              </div>
            );
          },
        },
      ],
    },
  };

  const pageSize = isLg ? 30 : 20;

  const { activityLink } = activityData;
  const activityCode = typeMap[tableType].reg ? activityLink.match(typeMap[tableType].reg)[0].replace(/\D/g, '') : '';

  useEffect(() => {
    getData();
  }, [activityCode, pageInfo]);

  function getData() {
    setIsLoading(true);
    const params = {
      ...pageInfo,
      pageSize,
    };
    if (['bk', 'qc'].includes(tableType)) {
      Object.assign(params, {
        sourcePage: 'home',
      });
    }
    sendAxios({
      method: 'get',
      url: typeMap[tableType].api.replace('{activityCode}', activityCode),
      baseURL: Domains.lcscListUrl,
      params,
    })
      .then((res) => {
        if (res.code === 200 && res.result) {
          setData(res.result);
        } else {
          setData({
            searchResult: {
              productRecordList: [],
            },
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  if (!activityLink && tableType !== 'bk') {
    return <Empty emptyTips="商品正在上架中，请耐心等待" />;
  }

  if (isLoading) {
    return <Loading isLg={isLg} />;
  }

  if (data) {
    const { searchResult } = data;
    if (searchResult.productRecordList.length < pageSize && pageInfo.currentPage > 1) {
      // 数量不够并且不是第一页，则取上一页补齐数量
      searchResult.productRecordList = searchResult.productRecordList.concat(
        prevList.slice(0, pageSize - searchResult.productRecordList.length)
      );
    }
    if (pageInfo.currentPage === 1) {
      prevList = [];
      if (searchResult.productRecordList.length === 0) {
        return <Empty emptyTips="商品正在上架中，请耐心等待" />;
      }
    } else {
      prevList = searchResult.productRecordList; // 数据赋值
    }
    const dataSource = searchResult.productRecordList.slice(0, 10);
    const dataSource2 = searchResult.productRecordList.slice(10, 20);

    const tablesDataSource = [dataSource, dataSource2];
    if (isLg) {
      tablesDataSource.push(searchResult.productRecordList.slice(20, 30));
    }
    if (['discount', 'clearance'].includes(activityType)) {
      // 最大折扣的拿取
      tablesDataSource.forEach((ds) => {
        if (!Utils.isEmpty(ds)) {
          ds[0].zdzk = Math.min(...ds.map((item) => item.discountPriceMaxDiscount).filter(Boolean));
        }
      });
    }

    const onRow = (productItem) => ({
      onMouseEnter: () => {
        setHoveredRowKey(productItem.productVO.productId);
      },
      onMouseLeave: () => {
        setHoveredRowKey(null);
      },
    });
    let columns = typeMap[tableType].columns;
    const goNextPage = () => {
      setPageInfo((pre) => {
        return {
          ...pre,
          currentPage: pre.currentPage === searchResult.countPage ? 1 : pre.currentPage + 1,
        };
      });
    };
    return (
      <>
        <div className="flex">
          {tablesDataSource.map((dataSource, idx) => {
            if (!Utils.isEmpty(dataSource)) {
              if (dataSource[0].zdzk) {
                const maxDiscount = MultiAccurate(dataSource[0].zdzk || 0, 10);
                // 屎山
                columns = cloneDeep(typeMap.qc.columns);
                columns[2].title = () => (
                  <span>
                    <span>折扣价</span>
                    {maxDiscount !== 10 ? (
                      <span className="inline-block text-center leading-[20px] rounded-[4px] w-[60px] h-[22px] border border-solid border-[#FFCCC7] ml-[4px] text-[12px] text-[#FF4D4F]">
                        最低{maxDiscount}折
                      </span>
                    ) : (
                      <></>
                    )}
                  </span>
                );
              }
              return (
                <Table
                  data-spm={spmType + (idx + 1)}
                  key={idx}
                  className={`${idx === 2 ? 'mr-0' : 'mr-[20px]'} ${isLg ? 'basis-[33.33%]' : 'basis-[50%]'}`}
                  size="small"
                  dataSource={dataSource}
                  columns={columns}
                  pagination={false}
                  onRow={onRow}
                  rowClassName={() => 'h-[45px]'}
                  bordered
                />
              );
            }
          })}
        </div>
        <div className="flex absolute bottom-[20px] left-[50%] translate-x-[-50%] justify-center mt-[20px]">
          {searchResult.countPage > 1 && (
            <Button
              className="w-[128px] h-[38px] mr-[60px] rounded-[22px] border border-black border-solid text-blborder-black font-bold"
              iconPosition="start"
              icon={<RedoOutlined className="relative top-[-3px]" />}
              onClick={goNextPage}
            >
              换一批
            </Button>
          )}
          <a
            href={tableType === 'bk' ? resourceData.projectConfigVO.lcscListUrl + '/hot.html' : activityLink}
            target="_blank"
            data-spm={activityType.slice(0, 3) + '.mr'}
          >
            <Button
              className="w-[128px] h-[38px] rounded-[22px] border border-lcBlue border-solid text-lcBlue font-bold"
              type="default"
            >
              查看更多
            </Button>
          </a>
        </div>
      </>
    );
  }
};

const StockComponent = ({ isLg }) => {
  const { resourceData } = useContext(LayoutContext);

  const adSize = isLg ? 'w-[321px] h-[553px]' : 'h-[553px] w-[257px]';
  const adBgImg = isLg
    ? `bg-[url('https://static.szlcsc.com/ecp/assets/newWeb/www/home/stock-big-bg.png')]`
    : `bg-[url('https://static.szlcsc.com/ecp/assets/newWeb/www/home/stock-bg.png')]`;

  return (
    <div className="flex justify-between" data-spm="stk">
      <div className={`relative ${adBgImg} bg-cover mr-[20px] ${adSize}`}>
        <a
          data-spm="mr"
          href={`${resourceData?.projectConfigVO?.lcscWwwUrl}/stock.html`}
          target="_blank"
          className="flex items-center absolute left-[92px] bottom-[20px] _lg:left-[127px] _lg:bottom-[30px] text-[14px] leading-[19px] text-[#54657D] hover:text-lcBlue"
        >
          <span className="mr-[4px]">了解更多</span>
          <DoubleRightOutlined className="mb-[0px] size-[12px]" />
        </a>
      </div>
      <QuickFindMaterialComponent isLg={isLg} backgroundColor="bg-gradient-to-b from-[#DCF2FF]" />
    </div>
  );
};

const GreatVoyageComponent = ({ isLg }) => {
  const ActivityStatusMap = {
    next: 'next',
    end: 'end',
    ing: 'ing',
  };
  const { resourceData } = useContext(LayoutContext);
  const [isLoading, setIsLoading] = useState(true);
  const [initInfo, setInitInfo] = useState({
    uuid: '',
    startTime: '',
    endTime: '',
    quotaNum: '',
    remainNum: '',
    isRegistered: '',
    approvalStatus: '',
    customerPhone: '',
    bindPhone: '',
    remark: '',
    initRegisterNum: '',
    endRegisterHour: '',
    endRegisterMinute: '',
  });
  const [activityStatus, setActivityStatus] = useState('');
  const [timeMap, setTimeMap] = useState({
    day: 0,
    hour: 0,
    minute: 0,
    second: 0,
  });
  const timer = useRef(null);

  useEffect(() => {
    getItpVoyageProjectInit();
  }, []);

  useEffect(() => {
    // 倒计时定时器
    timer.current = setInterval(() => {
      setTimeMap((prev) => {
        let { day, hour, minute, second } = prev;
        if (day === 0 && hour === 0 && minute === 0 && second === 0) {
          clearInterval(timer.current);
          return prev;
        }
        if (second > 0) {
          second--;
        } else {
          second = 59;
          if (minute > 0) {
            minute--;
          } else {
            minute = 59;
            if (hour > 0) {
              hour--;
            } else {
              hour = 23;
              if (day > 0) {
                day--;
              }
            }
          }
        }
        return { day, hour, minute, second };
      });
    }, 1000);
    if (initInfo?.startTime && initInfo?.endTime) {
      const now = dayjs();
      const start = dayjs(initInfo?.startTime);
      const end = dayjs(initInfo?.endTime);
      if (start.isAfter(now)) {
        const { day, hour, minute, second } = getTimeMap(now.format('YYYY-MM-DD HH:mm:ss'), initInfo?.startTime);
        setTimeMap({
          day,
          hour,
          minute,
          second,
        });
        setActivityStatus(ActivityStatusMap.next);
      } else if (end.isBefore(now) || initInfo.remainNum === 0) {
        setTimeMap({
          day: 0,
          hour: 0,
          minute: 0,
          second: 0,
        });
        setActivityStatus(ActivityStatusMap.end);
      } else if (now.isAfter(start) && now.isBefore(end)) {
        const { day, hour, minute, second } = getTimeMap(now.format('YYYY-MM-DD HH:mm:ss'), initInfo?.endTime);
        setTimeMap({
          day,
          hour,
          minute,
          second,
        });
        setActivityStatus(ActivityStatusMap.ing);
      }
    }
    return () => {
      clearInterval(timer.current);
    };
  }, [initInfo?.startTime, initInfo?.endTime, initInfo.remainNum]);

  const getTimeMap = (startStr, endStr) => {
    const start = dayjs(startStr);
    const end = dayjs(endStr);
    // 如果当前时间 >= 结束时间，则返回 0 值
    if (start.isAfter(end) || start.isSame(end)) {
      return { day: 0, hour: 0, minute: 0, second: 0 };
    }
    const diff = end.diff(start, 'second'); // 总秒数差值
    const day = Math.floor(diff / (24 * 60 * 60));
    const hour = Math.floor((diff % (24 * 60 * 60)) / 3600);
    const minute = Math.floor((diff % 3600) / 60);
    const second = diff % 60;
    return { day, hour, minute, second };
  };

  const getItpVoyageProjectInit = () => {
    setIsLoading(true);
    sendAxios({
      method: 'get',
      url: '/itp/voyage/project/init',
      baseURL: Domains.lcscActivityUrl,
    })
      .then(async (res) => {
        if (res?.code === 200) {
          if (res?.result) {
            const customerPhone = await decryptSensitiveData(res?.result?.customerPhone);
            setInitInfo({
              uuid: res?.result?.uuid,
              startTime: res?.result?.startTime,
              endTime: res?.result?.endTime,
              quotaNum: res?.result?.quotaNum,
              remainNum: res?.result?.remainNum,
              isRegistered: res?.result?.isRegistered,
              approvalStatus: res?.result?.approvalStatus,
              customerPhone,
              bindPhone: res?.result?.bindPhone,
              remark: res?.result?.remark,
              initRegisterNum: res?.result?.initRegisterNum,
              endRegisterHour: res?.result?.endRegisterHour,
              endRegisterMinute: res?.result?.endRegisterMinute,
            });
          } else {
            clearInterval(timer.current);
            setActivityStatus(ActivityStatusMap.end);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const pad = (n) => {
    return String(n).padStart(2, '0');
  };

  const brandList = useMemo(() => {
    if (isLg) {
      return [
        'TI(德州仪器)',
        'ST(意法半导体)',
        'SAMSUNG(三星)',
        'muRata(村田)',
        'Nexperia(安世)',
        'onsemi(安森美)',
        'UNI-ROYAL(厚声)',
        'FH(风华)',
        'CJ(江苏长电/长晶)',
        'EPSON(爱普生)',
        'Kyocera AVX',
        'MDD(辰达半导体)',
      ];
    } else {
      return [
        'TI(德州仪器)',
        'ST(意法半导体)',
        'SAMSUNG(三星)',
        'muRata(村田)',
        'UNI-ROYAL(厚声)',
        'FH(风华)',
        'CJ(江苏长电/长晶)',
        'EPSON(爱普生)',
      ];
    }
  }, [isLg]);

  const ActivityDetailUrl = `${env === 'fat' ? 'https://fat-activity-next.szlcsc.com' : resourceData?.projectConfigVO?.lcscActivityUrl}/sea_patrol.html`;

  const handleBannerClick = () => {
    buriedPoint({
      pf: 'pc',
      cp: 'index',
      ty: 'click',
      op: 'Activity_Free_picking',
      data: { hf: ActivityDetailUrl },
    });
  };

  const goActivityDetail = () => {
    buriedPoint({
      pf: 'pc',
      cp: 'index',
      ty: 'click',
      op: activityStatus === ActivityStatusMap.ing ? 'Activity_Sign_up' : 'Activity_details',
      data: { hf: ActivityDetailUrl },
    });
    window.open(ActivityDetailUrl, '_blank');
  };

  const _loadWx = () => {
    buriedPoint({
      pf: 'pc',
      cp: 'index',
      ty: 'click',
      op: 'Activity_Consultation',
      data: { hf: 'https://work.weixin.qq.com/kfid/kfc5deddba351c362c2' },
    });

    const name = 'wxWindow';
    const iHeight = 600;
    const iWidth = 750;

    const iTop = (window.screen.height - 30 - iHeight) / 2; //获得窗口的垂直位置;
    const iLeft = (window.screen.width - 10 - iWidth) / 2; //获得窗口的水平位置;

    window.open(
      'https://work.weixin.qq.com/kfid/kfc5deddba351c362c2',
      name,
      'height=' +
        iHeight +
        ',innerHeight=' +
        iHeight +
        ',width=' +
        iWidth +
        ',innerWidth=' +
        iWidth +
        ',top=' +
        iTop +
        ',left=' +
        iLeft +
        ',toolbar=no,menubar=no,scrollbars=auto,resizeable=no,location=no,status=no'
    );
  };

  if (isLoading) {
    return <Loading isLg={isLg} />;
  }

  const renderCountdown = () => {
    return (
      <>
        <div
          className={
            'w-[51px] h-[44px] leading-[44px] text-center bg-[#0093E6] rounded-[6px] shadow-[inset_1px_2px_1px_1px_rgba(61,2,2,0.23)] text-[24px] text-white font-bold mr-[11px]'
          }
        >
          {pad(timeMap.day)}
        </div>
        <div className={'text-[#666666] text-[16px] leading-[26px]'}>天</div>
        <div
          className={
            'w-[51px] h-[44px] leading-[44px] text-center bg-[#0093E6] rounded-[6px] shadow-[inset_1px_2px_1px_1px_rgba(61,2,2,0.23)] text-[24px] text-white font-bold ml-[12px] mr-[11px]'
          }
        >
          {pad(timeMap.hour)}
        </div>
        <div className={'text-[#666666] text-[16px] leading-[26px]'}>时</div>
        <div
          className={
            'w-[51px] h-[44px] leading-[44px] text-center bg-[#0093E6] rounded-[6px] shadow-[inset_1px_2px_1px_1px_rgba(61,2,2,0.23)] text-[24px] text-white font-bold ml-[12px] mr-[11px]'
          }
        >
          {pad(timeMap.minute)}
        </div>
        <div className={'text-[#666666] text-[16px] leading-[26px]'}>分</div>
        <div
          className={
            'w-[51px] h-[44px] leading-[44px] text-center bg-[#0093E6] rounded-[6px] shadow-[inset_1px_2px_1px_1px_rgba(61,2,2,0.23)] text-[24px] text-white font-bold ml-[12px] mr-[11px]'
          }
        >
          {pad(timeMap.second)}
        </div>
        <div className={'text-[#666666] text-[16px] leading-[26px]'}>秒</div>
      </>
    );
  };

  const renderRemainNum = () => {
    const numList = String(initInfo?.remainNum || 0)
      .padStart(4, '0')
      .split('')
      .map(Number);
    return (
      <>
        <div className={'text-[#666666] text-[16px] leading-[26px] mr-[10px]'}>剩余名额</div>
        {numList.map((item, index) => {
          return (
            <div
              key={index}
              className={
                'w-[51px] h-[44px] leading-[44px] text-center bg-[#0093E6] rounded-[6px] shadow-[inset_1px_2px_1px_1px_rgba(61,2,2,0.23)] text-[24px] text-white font-bold  mr-[12px]'
              }
            >
              {item}
            </div>
          );
        })}
        <div className={'text-[#666666] text-[16px] leading-[26px]'}>个</div>
      </>
    );
  };

  const renderInitRegisterNum = () => {
    const numList = String(initInfo?.initRegisterNum || 0)
      .padStart(4, '0')
      .split('')
      .map(Number);
    return (
      <>
        <div className={'text-[#666666] text-[16px] leading-[26px] mr-[10px]'}>已领名额</div>
        {numList.map((item, index) => {
          return (
            <div
              key={index}
              className={
                'w-[51px] h-[44px] leading-[44px] text-center bg-[#0093E6] rounded-[6px] shadow-[inset_1px_2px_1px_1px_rgba(61,2,2,0.23)] text-[24px] text-white font-bold  mr-[12px]'
              }
            >
              {item}
            </div>
          );
        })}
        <div className={'text-[#666666] text-[16px] leading-[26px]'}>个</div>
        <div className={'w-[219px] h-[60px] absolute right-[-200px] top-[-14px]'}>
          {(isNotEmpty(initInfo?.endRegisterHour) || isNotEmpty(initInfo?.endRegisterMinute)) && (
            <div className={'text-white text-[12px] absolute left-[0px] top-[-6px] text-center'}>
              <div className="text-center leading-[22px] h-[22px] rounded-[4px] px-[13px] bg-[#FF4D4F]">
                {isNotEmpty(initInfo?.endRegisterHour) && <span>{initInfo?.endRegisterHour}小时</span>}
                {isNotEmpty(initInfo?.endRegisterMinute) && <span>{initInfo?.endRegisterMinute}分钟</span>}
                领完
              </div>
              <div
                className={'w-[0px] h-[0px] ml-[8px] rotate-[30deg] mt-[-4px]'}
                style={{
                  borderLeft: '3px solid transparent',
                  borderRight: '5px solid transparent',
                  borderTop: '10px solid #FF4D4F',
                }}
              ></div>
            </div>
          )}
        </div>
      </>
    );
  };

  const renderTitle = () => {
    return activityStatus === ActivityStatusMap.next ? (
      <span>大航海计划-活动开始倒计时</span>
    ) : activityStatus === ActivityStatusMap.ing ? (
      <span>大航海计划-报名火热进行中</span>
    ) : activityStatus === ActivityStatusMap.end ? (
      <span>大航海计划-本期报名已结束 敬请期待下期～</span>
    ) : (
      <></>
    );
  };

  // 共用的左侧横幅组件
  const renderBanner = () => (
    <div
      className={`relative bg-[url('https://static.szlcsc.com/ecp/assets/newWeb/www/home/great-vayage-banner.png')] bg-cover mr-[20px] w-[321px] h-[553px]`}
    >
      <a
        data-spm="mr"
        href={ActivityDetailUrl}
        target="_blank"
        className="flex items-center absolute left-[50%] translate-x-[-50%] bottom-[30px] text-[14px] leading-[19px] text-[#54657D] hover:text-lcBlue"
        onClick={handleBannerClick}
      >
        <span className="mr-[4px]">了解活动详情</span>
        <RightOutlined className="size-[12px]" />
      </a>
    </div>
  );

  // 当活动结束时，使用 QuickFindMaterialComponent 替换右侧部分
  if (activityStatus === ActivityStatusMap.end) {
    return (
      <div className="flex justify-between" data-spm="gv">
        {renderBanner()}
        <QuickFindMaterialComponent isLg={isLg} backgroundColor="bg-gradient-to-b from-[#E8F0FC] to-white" />
      </div>
    );
  }

  return (
    <div className="flex justify-between" data-spm="gv">
      {renderBanner()}
      <div className="relative flex flex-col _lg:w-[1171px] w-[819px] h-[553px] rounded-[12px] overflow-hidden bg-[url('https://static.szlcsc.com/ecp/assets/newWeb/www/home/great-vayage-bg.png')] bg-no-repeat bg-[top_center]">
        <div className="absolute top-0 w-full flex items-center justify-center h-[43px] rounded-t-[12px] bg-gradient-to-l from-transparent via-[#3293f2fc] to-transparent">
          <img
            className="mr-[10px] w-[18px] h-[16px]"
            src="https://static.szlcsc.com/ecp/assets/newWeb/www/home/horn.svg"
            alt="icon"
          />
          <p className="text-[#fff] text-[14px]">
            立创推出「大航海计划」，为工程师提供免费元器件、0元打板券等福利，降低研发成本高效完成项目验证
          </p>
        </div>
        <div className="relative flex-1 p-[150px_24px_24px]">
          <div className="flex flex-col items-center">
            <div className="absolute top-[70px] h-[63px] flex flex-col items-center justify-center">
              <p className="text-[24px] font-bold">{renderTitle()}</p>
              {activityStatus === ActivityStatusMap.next && (
                <p className="mt-[6px] text-[14px]">{dayjs(initInfo?.startTime).format('YYYY年M月D日HH:mm开始')}</p>
              )}
            </div>
            {activityStatus === ActivityStatusMap.next ? (
              <div className="flex items-center">{renderCountdown()}</div>
            ) : activityStatus === ActivityStatusMap.ing ? (
              <div className="flex items-center">{renderRemainNum()}</div>
            ) : activityStatus === ActivityStatusMap.end ? (
              <div className="flex items-center relative">{renderInitRegisterNum()}</div>
            ) : (
              <></>
            )}
            <div className="mt-[50px] flex items-center">
              <FlashButton
                className="w-[173px] h-[50px] text-[16px] bg-[#0093E6] hover:bg-[#47B2ED]"
                onClick={goActivityDetail}
              >
                {activityStatus === ActivityStatusMap.ing ? '立即报名' : '了解活动详情'}
              </FlashButton>
              <button
                className="ml-[18px] w-[145px] h-[50px] bg-[#CFEEFF] border border-[#0093E6] text-[#0093E6] text-[16px] rounded-full hover:bg-[#0093E6] hover:text-[#fff]"
                onClick={_loadWx}
              >
                咨询客服
              </button>
            </div>
          </div>
          <div className="mt-[62px] flex items-end">
            <span className="text-[18px] font-bold">
              免费料<span className="text-[#199DE9]">品牌</span>
            </span>
            <span className="ml-[16px] text-[14px] text-[#666666]">
              目前可提供近<span className="text-[#000]">400个</span>免费物料型号，覆盖品牌
              <span className="text-[#000]">30+个</span>
              ，持续增加中...
            </span>
          </div>
          <div className="mt-[16px] h-[132px] bg-[#FEFEFF] rounded-[10px] flex flex-wrap justify-between p-[30px_34px_0]">
            {brandList.map((item, index) => (
              <span key={index} className={`${isLg ? 'w-[16%]' : 'w-[21%]'} text-[16px]`}>
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MroComponent = ({ activityData, handleClickGoBuy, isLg, spmType }) => {
  const { activityLink } = activityData;
  const pageSize = isLg ? 16 : 12;

  const { resourceData } = useContext(LayoutContext);
  const [pageInfo, setPageInfo] = useState({ pageNumber: 1 });

  const [mroData, setMroData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    getData();
  }, [pageInfo]);

  function getData() {
    setIsLoading(true);
    const ltCode = activityLink.match(/\/zk\d+\.html/)[0].replace(/\D/g, '');
    sendAxios({
      method: 'get',
      url: '/zk' + ltCode,
      baseURL: Domains.lcscListUrl,
      params: { ...pageInfo, pageSize },
    })
      .then((res) => {
        if (res.code === 200 && res.result) {
          setMroData(res.result);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }
  if (isLoading) {
    return <Loading isLg={isLg} />;
  }
  function ItemCard({ data, isRowLast, sort }) {
    // 折扣价,如果没有折扣价,该位置就显示原价
    const orgPrice = data.productVO.productPriceList[data.productVO.productPriceList.length - 1].thePrice;
    let discountPrice = orgPrice;
    if (!Utils.isEmpty(data.productVO.productPriceDiscountList)) {
      discountPrice = Math.min(...data.productVO.productPriceDiscountList[0].priceList.map((t) => t.price));
    }
    return (
      <li
        className={`custom-group relative w-[172px] h-[242px] pt-[10px] pb-[12px] px-[14px] ${isRowLast ? 'mr-[0]' : 'mr-[20px]'} border border-solid border-transparent hover:border-lcBlue rounded-[10px] mb-[10px]`}
      >
        <a
          href={resourceData.projectConfigVO.lcscItemUrl + '/' + data.productVO.productId + '.html'}
          target="_blank"
          data-spm={sort}
        >
          {/* 别显示10折了 */}
          {data.discountPriceMaxDiscount && data.discountPriceMaxDiscount !== 1 && (
            <span className="absolute text-center left-[14px] leading-[20px] text-white top-[10px] w-[40px] h-[20px] bg-cover bg-[#FF4960] rounded-br-[4px] rounded-tl-[4px]">
              {MultiAccurate(data.discountPriceMaxDiscount || 0, 10)}折
            </span>
          )}
          <img
            src={data.productVO.breviaryImageUrl || '//static.szlcsc.com/ecp/assets/web/static/images/default_pic.gif'}
            className="size-[140px] rounded-[10px]"
            onError={(event) => {
              event.target.src = '//static.szlcsc.com/ecp/assets/web/static/images/default_pic.gif';
            }}
          />
          <p
            className="mt-[6px] text-[14px] text-[#0B1832] truncate"
            title={data.lightProductName || data.productVO.productName}
          >
            {data.lightProductName || data.productVO.productName}
          </p>
        </a>
        <div className="mt-[15px] flex justify-between items-center">
          <div className="text-[#FF4960]">
            <div className="leading-none">
              <span className="text-[12px]">￥</span>
              <span className="text-[18px]">{discountPrice}</span>
            </div>
            {orgPrice !== discountPrice && (
              <span className="text-[#B8BEC9] line-through text-[10px]">￥{orgPrice}</span>
            )}
          </div>
          <button
            className="size-[24px] rounded-[50%] bg-cover custom-group-hover-cart-icon-2"
            onClick={() => handleClickGoBuy(data, activityData.activityType)}
          ></button>
        </div>
      </li>
    );
  }
  if (resourceData) {
    if (activityLink) {
      if (mroData) {
        const { searchResult } = mroData;
        if (searchResult.productRecordList.length < pageSize && pageInfo.pageNumber > 1) {
          // 数量不够并且不是第一页，则取上一页补齐数量
          searchResult.productRecordList = searchResult.productRecordList.concat(
            mroBkPrevList.slice(0, pageSize - searchResult.productRecordList.length)
          );
        }
        if (pageInfo.pageNumber === 1) {
          mroBkPrevList = [];
          if (searchResult.productRecordList.length === 0) {
            return <Empty emptyTips="商品正在上架中，请耐心等待" />;
          }
        } else {
          mroBkPrevList = searchResult.productRecordList; // 数据赋值
        }
        const goNextPage = () => {
          setPageInfo((pre) => {
            return {
              ...pre,
              pageNumber: pre.pageNumber === searchResult.countPage ? 1 : pre.pageNumber + 1,
            };
          });
        };

        return (
          <div>
            <ul className="flex flex-wrap" data-spm={spmType}>
              {searchResult.productRecordList.map((item, idx) => (
                <ItemCard
                  key={idx}
                  sort={idx + 1}
                  data={item}
                  isRowLast={pageSize / 2 === idx + 1 || idx === searchResult.productRecordList.length - 1}
                ></ItemCard>
              ))}
            </ul>
            <div className="flex absolute bottom-[20px] left-[50%] translate-x-[-50%] justify-center mt-[20px]">
              {searchResult.countPage > 1 && (
                <Button
                  className="w-[128px] h-[38px] mr-[60px] rounded-[22px] border border-black border-solid text-blborder-black font-bold"
                  iconPosition="start"
                  icon={<RedoOutlined className="relative top-[-3px]" />}
                  onClick={goNextPage}
                >
                  换一批
                </Button>
              )}
              <a href={activityLink} target="_blank" data-spm="mro.mr">
                <Button
                  className="w-[128px] h-[38px] rounded-[22px] border border-lcBlue border-solid text-lcBlue font-bold"
                  type="default"
                >
                  查看更多
                </Button>
              </a>
            </div>
          </div>
        );
      }
    } else {
      return <Empty emptyTips="商品正在上架中，请耐心等待" />;
    }
  }
};

const IndexActivityAd = ({ data, isLg }) => {
  if (!data) {
    return;
  }

  const { activityList } = data;
  const { resourceData } = useContext(LayoutContext);
  const projectConfigVO = resourceData?.projectConfigVO;
  const [currentActivity, setCurrentActivity] = useState(activityList[0]); //当前选中的活动

  function handleActivityChange(head) {
    setCurrentActivity(head);
    // 埋点
    indexBuriedPoint(indexBuriedPointActivityTabsMap[head.activityType] || '未定义op');
  }
  if (!Utils.isEmpty(activityList)) {
    // 加购弹窗 start
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [buyItemProductId, setBuyItemPoductId] = useState('');
    const [buyRow, setBuyRow] = useState({});

    const [isShowBuyGroupProductDialog, setIsShowBuyGroupProductDialog] = useState(false);

    const clickGoBuy = (row, activityType) => {
      const dialogRightProps = {
        activityType,
        rebate: !row.isGuidePrice && row.rebate ? row.rebate : '',
        discountPriceMaxDiscount: row.productVO.discountBuyLimitProduct ? '' : row.discountPriceMaxDiscount || '',
      };
      lcscShowAddCartModalAjax(row.productVO, dialogRightProps);
    };
    const clickLtGoBuy = (row) => {
      setBuyRow(row);
      setIsShowBuyGroupProductDialog(true);
    };

    const lcscShowAddCartModalAjax = (row, dialogRightProps) => {
      sendAxios({
        method: 'get',
        url: `${API.checkStatus}?productId=${row.productId}`,
        baseURL: Domains.lcscCartUrl,
      }).then((data) => {
        if (data.result.status == 0) {
          message.error('商品不存在!');
        } else if (data.result.status == 1 || data.result.status == 4) {
          message.error('该商品已下架!');
        } else if (data.result.status == 2) {
          const msgUp =
            '商品' +
            data.result.productCode +
            '已有替代商品<a href="' +
            projectConfigVO.lcscItemUrl +
            '/' +
            data.result.productId +
            '.html" target="_blank" id="shopping_cart_info_link">' +
            data.result.productRepeatCode +
            '（查看替代商品）</a>，不接受订货';
          message.error(msgUp);
        } else {
          const res = data?.result;
          const priceList = cloneDeep(res?.productPriceList || []);
          if (res.productDiscountPriceList && res.productDiscountPriceList.length) {
            res.productDiscountPriceList.forEach((item, index) => {
              if (res.productDiscountPriceList.length > priceList.length) {
                priceList.push({ productPrice: null, startPurchasedNumber: null });
              }
              priceList[index].productPrice = item.price;
              priceList[index].startPurchasedNumber = item.spNumber;
            });
          }
          const dataObj = { ...data?.result, entryType: 'home_page' };
          dataObj.ratio = res.convesionRatio == 0 ? 1 : res.convesionRatio;
          dataObj.cartPrice = BasicRound(priceList[0]?.productPrice || 0, 6);
          dataObj.productPriceList = priceList;
          dataObj.minEncapsulationNumber = res.productMinEncapsulationNumber;
          dataObj.hasStockNow = row.hasStockNow;
          dataObj.minBuyNumber = row.minBuyNumber;
          dataObj.dialogRightProps = dialogRightProps;
          dataObj.originalPriceList = res?.productPriceList || []; // 原价
          setBuyRow(dataObj);
          setBuyItemPoductId(row.productId);
          setIsModalOpen(true);
        }
      });
    };

    // 加购弹窗 end

    const pageTabNum = 6;

    return (
      <>
        {/* 加购弹窗 */}
        {isModalOpen && (
          <GoBuyDialog
            row={buyRow}
            isModalOpen={isModalOpen}
            productId={buyItemProductId}
            entryType="home_page"
            setIsModalOpen={(val) => setIsModalOpen(val)}
          />
        )}
        {/* 点击立推弹窗 */}
        {isShowBuyGroupProductDialog && (
          <BuyGroupProductDialog
            data={buyRow}
            isShowBuyGroupProductDialog={isShowBuyGroupProductDialog}
            setIsShowBuyGroupProductDialog={(val) => setIsShowBuyGroupProductDialog(val)}
          />
        )}
        <section className="relative mx-auto mt-[20px] w-[1200px] _lg:w-[1552px] h-[673px] bg-white rounded-[20px] py-[20px] px-[18px]">
          <Swiper
            className="relative w-full h-[60px] overflow-hidden mx-auto mb-[20px]"
            wrapperTag="ul"
            tag="div"
            modules={[Navigation]}
            spaceBetween={0}
            slidesPerView={pageTabNum}
            onSlideChange={(swp) => {
              const preBtn = document.getElementById('ac-tab-custom-prev-button');
              const nextBtn = document.getElementById('ac-tab-custom-next-button');
              if (preBtn) {
                preBtn.style.display = swp.isBeginning ? 'none' : 'flex';
              }
              if (nextBtn) {
                nextBtn.style.display = swp.isEnd ? 'none' : 'flex';
              }
            }}
            navigation={{
              nextEl: '#ac-tab-custom-next-button',
              prevEl: '#ac-tab-custom-prev-button',
            }}
          >
            {activityList.map((head, idx) => (
              <SwiperSlide
                key={idx}
                tag="li"
                className={`h-[60px] index-ac-tabs shrink-0 cursor-pointer flex items-center border-gradient custom-group hover:text-lcBlue !important _lg:pr-[25px] pr-[18px] ${idx === activityList.length - 1 ? 'no-border-gradient' : ''} ${idx === 0 ? 'pl-0' : '_lg:pl-[25px] pl-[18px]'} ${head.activityType === currentActivity.activityType ? 'text-lcBlue' : 'text-black'}`}
                onClick={() => handleActivityChange(head)}
              >
                <img
                  className="inline-block lg:size-[60px] size-[42px] rounded-[50%]"
                  src={head.activityLogo}
                  alt={head.mainTitle}
                />
                <div className="_lg:ml-[20px] ml-[10px]">
                  <h3
                    className={`font-bold text-[16px] _lg:text-[18px] custom-group-hover-text-lcBlue mb-[4px] _lg:mb-[8px] antialiased ${head.activityType === currentActivity.activityType ? 'text-lcBlue' : 'text-[#000]'}`}
                  >
                    {head.mainTitle}
                  </h3>
                  <p
                    className={`_lg:text-[14px] text-[12px] ${head.activityType === currentActivity.activityType ? 'text-lcBlue' : 'text-[#69788A]'} custom-group-hover-text-lcBlue antialiased`}
                  >
                    {head.viceTitle}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
          {/* 自定义导航按钮 */}
          {activityList.length > pageTabNum && (
            <>
              <div
                id="ac-tab-custom-prev-button"
                className="absolute top-[20px] left-[18px] hidden items-center size-[60px] bg-gradient-to-r from-[#fff] z-[1]"
              >
                <button className="rotate-[180deg] size-[24px] bg-[url(https://static.szlcsc.com/ecp/assets/newWeb/www/home/more-tab-icon.svg)]"></button>
              </div>
              <div
                id="ac-tab-custom-next-button"
                className="absolute top-[20px] right-[18px] flex items-center justify-end size-[60px] bg-gradient-to-l from-[#fff]  z-[1]"
              >
                <button className="size-[24px] bg-[url(https://static.szlcsc.com/ecp/assets/newWeb/www/home/more-tab-icon.svg)]"></button>
              </div>
            </>
          )}
          {/* 备货 */}
          {currentActivity.activityType === 'stockpile' && (
            <StockComponent activityData={currentActivity} isLg={isLg}></StockComponent>
          )}
          {/* 大航海 */}
          {currentActivity.activityType === 'great_voyage' && <GreatVoyageComponent isLg={isLg}></GreatVoyageComponent>}
          {/* 立推 */}
          {currentActivity.activityType === 'push_material' && (
            <TableDataComponent
              spmType={activityTypeSpmMap['push_material']}
              tableType="lt"
              activityData={currentActivity}
              handleClickGoBuy={clickLtGoBuy}
              isLg={isLg}
            ></TableDataComponent>
          )}
          {/* 爆款 */}
          {currentActivity.activityType === 'hot' && (
            <TableDataComponent
              tableType="bk"
              spmType={activityTypeSpmMap['hot']}
              activityData={currentActivity}
              handleClickGoBuy={clickGoBuy}
              isLg={isLg}
            ></TableDataComponent>
          )}
          {/* 清仓 、 折扣 、返采购金 */}
          {['discount', 'clearance', 'rebate'].includes(currentActivity.activityType) && (
            <TableDataComponent
              tableType="qc"
              spmType={activityTypeSpmMap[currentActivity.activityType]}
              key={currentActivity.activityType}
              activityData={currentActivity}
              handleClickGoBuy={clickGoBuy}
              isLg={isLg}
            ></TableDataComponent>
          )}
          {/* MRO爆款 */}
          {currentActivity.activityType === 'mro_hot' && (
            <MroComponent
              spmType={activityTypeSpmMap['mro_hot']}
              activityData={currentActivity}
              handleClickGoBuy={clickGoBuy}
              isLg={isLg}
            ></MroComponent>
          )}
        </section>
      </>
    );
  }
};

export default IndexActivityAd;
